/* eslint-disable max-len */
import { makeStyles, Theme } from '@material-ui/core';
import { AppAPI } from '@moderepo/mode-apis';
import { capitalizeAll, capitalizeFirst, ITEMS_PER_PAGE_10, GenericMessageBox, useSearchParams, PaginationBar, PAGING_MIN_PAGES, BaseListCompField,
    ITEMS_PER_PAGE_LIST } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { userAppDataContext, selectEntities, fetchEntities, UIFetchEntityFilters, UserAppDataState, UserAppDataStateAction, ExtDispatch,
    clearEntities } from '@moderepo/mode-data-state-user-app';
import { Agent, AgentRole, Entity, EntityAttributeParam, EntityAttributeType, EntityAttributeValueType, EntityClass, EntitySortFieldType, SortOrder }
    from '@moderepo/mode-internal-apis';
import { modeUIContext, removeControlPanelComp, addControlPanelComp, showSlideOutPanel, SlideOutPanelLocation, selectCurrentUsageMode, UsageMode,
    ControlPanelCompPriority } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import produce, { castDraft, Draft } from 'immer';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { convertAttributesQueryToString, EntitiesListingControlBar, EntitiesListingControlBarProps, EntityBasicViewDesign,
    EntityBasicViewUIComponentCustomSettings, findEntityNode, getEntityDisplayName, getFilterableAttributes,
    getNumericAttributeCompareOperatorDisplayName, getStringAttributeCompareOperatorDisplayName, SORTABLE_BASIC_ENTITY_ATTRIBUTES,
    SORTABLE_CUSTOM_ATTRIBUTE_TYPES, splitEntitySortFieldInfo, SUPPORTED_OPERATORS_BY_ATTRIBUTE_TYPE, useSelectTranslatedEntityClasses } from '.';
import { RouteHelper } from '..';
import { EntitiesListingComp, EntitiesListingCompProps, EntitiesListingDataItem, EntitiesListingTableViewCompProps, EntitiesListingViewType,
    EntityBreadcrumbBar, ENTITY_CUSTOM_ATTRIBUTES_COLUMN_PREFIX, ENTITY_DESCENDANT_COUNTS_COLUMN_PREFIX, findSelectedEntityNode } from '../../components';
import { useRitzTranslation } from '../../utils/customHooks';
import { useGetAllProjectHomes, useSelectEntity } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { EntitiesTableViewSettings } from '../../utils/ritzUIExtensionUtils';
import { ReadonlyNonEmptyArray, getEntitiesFeatureSettings, Mutable } from '../../utils/utils';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { EntityPreviewCompOptions, SelectEntityCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { EntityNode, ROOT_ENTITY_PARENT_ID, AttributesQuery } from './utils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        controlBarContainer: {
            top            : 0,
            zIndex         : 2,
            backgroundColor: theme.palette.background.default,
            borderRadius   : 0,
        },

        breadcrumbBar: {
            marginBottom: theme.spacing(1),
        },

        entitiesListingCompWrapper: {
            width    : '100%',
            height   : '100%',
            display  : 'flex',
            minHeight: '100%',
        },

        entitiesListingComp: {
            width: '100%',
        },

        [theme.breakpoints.up('md')]: {
            breadcrumbBar: {
                marginBottom: theme.spacing(2),
            },
        },

        [theme.breakpoints.up('lg')]: {
            breadcrumbBar: {
                marginBottom: theme.spacing(3),
            },
        },
    };
}, { name: 'EntitiesListingContainer', index: 1 });


/**
 * Because of pagination, some branches in the entity tree might not have all the children loaded and therefore we need to show something
 * to allow the user to click on to trigger the container to fetch more data. We will show a "Load More" button for any branch where
 * the parent entity has more children to load. We will display the "Load More" button like any other entity. Because we use a TreeView
 * and every tree item need to have some ID and this load more button is displayed like an entity, we have to give it some ID. Also, when
 * this button is clicked, we need to know which branch it is on, which parent entity it belonged to so that we know entity to load
 * more children for. So the solution is to use the parentId as the "load more" tree item's id. But we can't use the parentId
 * alone because it will be conflict with the parent node. We have to add some prefix to this id and the prefix will be the word "LOAD_MORE:"
 * So if a parentId is "abc1234", this load more tree item's id will be "LOAD_MORE:abc123".
 */
const LOAD_MORE_NODE_ID_PREFIX = 'LOAD_MORE:';


// The parent id to use when we fetch entities with search and filter params. We can't use 'root' or a specific entityId because
// that mean the results will only include entities direct child of that parentId.
const SEARCH_FILTER_ROOT_ENTITY_PARENT_ID = '';


const ENTITY_LISTING_PAGE_CONTROL_BAR = 'Entities_Listing_Control_Bar';



// Create the "Load More" tee item's ID be concatenating the prefix with the parent entity's id
const createLoadMoreEntityNodeId = (entityId: string) => {
    return `${LOAD_MORE_NODE_ID_PREFIX}${entityId}`;
};

// Parse the "load more" tree item id and get back parent entity id by removing the prefix from the load more item id.
const getEntityIdFromLoadMoreEntityNode = (fakeEntityId: string) => {
    return fakeEntityId.substring(LOAD_MORE_NODE_ID_PREFIX.length);
};



/**
 * There are a few places where we need to create some Dummy Entity object. FOr example, we need to create a dummy entity for the
 * root and we also create dummy entity for each "load more" entity node. Because it is a dummy entity, we don't care about any other
 * attributes except the entityId.
 */
const createDummyEntity = (entityId: string): Entity => {
    return {
        entityId,               // Dummy entity only need the entityId. All other attributes can have any fake value.
        createdAt      : '',
        updatedAt      : '',
        displaySettings: {
            name       : '',
            description: '',
        },
        entityClass: '',
        projectId  : 0,
        timeZone   : '',
        homeId     : 0,
        parentId   : undefined,
        metrics    : [],
    };
};



/**
 * Localize the entity.attributes' values for attributes which schema has `options` definition.
 *
 * Given:
 *      attributeValues: {
 *          name: "aaa",
 *          serialNumber: "123xyz"
 *      },
 *      attributeSchema: [{
 *          key: "name",
 *          options": [{
 *              value: "aaa", label: "AAA"
 *          }, {
 *              value: "bbb", label: "BBB"
 *          }]
 *      }, {
 *          key: "serialNumber",
 *          // no options
 *      }]
 *
 * Return:
 *      {
 *          name: "AAA",
 *          serialNumber: "123xyz"
 *      }
 */
const localizeEntityInstanceAttributesValues = (
    attributeValues: {
        [key: string]: EntityAttributeValueType | undefined;
    } | undefined,
    attributeSchema: readonly EntityAttributeParam<EntityAttributeValueType>[] | undefined,
): {
    [key: string]: EntityAttributeValueType | undefined;
} | undefined => {
    if (attributeValues && attributeSchema) {

        // This will loop through all the key/value pairs in the "attributeValues" map
        return Object.entries(attributeValues).reduce((result, [key, value]) => {

            // For each key, it looks up the schema from the attributeSchema that matches the attribute's key
            const attrSchema = attributeSchema.find((schema) => {
                return schema.key === key;
            });

            // If the schema contains "options" then continue to check for the attribute's "label" (displayname).
            if (attrSchema && attrSchema.options) {

                // Find the option from the schema.options array that match the attribute's value
                const option = attrSchema.options.find((option) => {
                    return option.value === value;
                });

                // If we found the option that match the value then we can use the "option.label" as the attribute's value. The "option.label"
                // should be the attribute value's displayName.
                if (option) {
                    return {
                        ...result,
                        [key]: option.label,
                    };
                }
            }

            // If we can't find the display name for the attribute value then display the value as is.
            return {
                ...result,
                [key]: value,
            };
        }, {});
    }

    // "attributeValues" is undefined or there is no schema therefore just return the same "attributeValues" value
    return attributeValues;
};


/**
 * Localize 1 single entity node and recursively localize it's children.
 */
const localizeSingleEntity = (
    entityNode: EntityNode,
    projectId: number,
    entityClasses: readonly EntityClass[],
    appDataState: UserAppDataState,
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    trans: TFunction,
): EntitiesListingDataItem => {

    const childEntityItems = entityNode.children.length > 0
        ? entityNode.children.map((child) => {
            return localizeSingleEntity(child, projectId, entityClasses, appDataState, appDataDispatch, trans);
        })
        : [];

    if (entityNode.hasMoreChildren && childEntityItems) {
        // if this entity node has more children that are not loaded, we will create a dummy entity node
        // and add it to the end of the childEntityItems array so that it will be shown in the UI as
        // 'load more...'
        const loadMoreEntityItemId = createLoadMoreEntityNodeId(entityNode.entity.entityId);

        childEntityItems.push({
            actualValue: {
                entity              : createDummyEntity(loadMoreEntityItemId),
                children            : [],
                hasMoreChildren     : false,
                numberOfPagesFetched: 0,
            },
            displayValue: {
                entityId: loadMoreEntityItemId,
                name    : trans('label.load_more'),
            },
            isLoadMoreItem: true,
        });
    }

    const entityClass = entityClasses.find((entityClass) => {
        return entityClass.entityClass === entityNode.entity.entityClass;
    });

    return {
        actualValue : entityNode,
        displayValue: {
            name       : entityNode.entity.displaySettings.name,
            entityId   : entityNode.entity.entityId,
            entityClass: entityClass?.displaySettings.name ?? entityNode.entity.entityClass,
            description: entityNode.entity.displaySettings.description,
            homeId     : entityNode.entity.homeId.toString(),
            parentId   : entityNode.entity.parentId && entityNode.entity.parentId !== ROOT_ENTITY_PARENT_ID
                ? getEntityDisplayName(projectId, entityNode.entity.parentId, appDataState, appDataDispatch) : '',
            icon      : entityNode.entity.displaySettings.iconData?.url ?? entityClass?.displaySettings.iconData?.url,
            image     : entityNode.entity.displaySettings.imageData?.url ?? entityClass?.displaySettings.imageData?.url,
            createdAt : getDateTimeAndElapsedTime(entityNode.entity.createdAt, trans, false),
            updatedAt : getDateTimeAndElapsedTime(entityNode.entity.updatedAt, trans, false),
            attributes: localizeEntityInstanceAttributesValues(entityNode.entity.attributes, entityClass?.attributeSchema),
            children  : childEntityItems,
        },
    };
};


/**
 * Given a root entity tree node and an entityId, get the path from the root node to the specified entityId. The return value should be
 * an array of entities starting from the root to the specified entityId.
 * If there is no path to the specified entityId, return undefined.
 */
const getPathToEntity = (root: EntityNode, entityId: string): readonly Entity[] | undefined => {
    // If the root node is the entity we are trying to find then return the root node as the only item in the path
    if (entityId === root.entity.entityId) {
        return [root.entity];
    }

    /**
     * If root node is not the entity we are looking for then check the root node's children. If we can find a path from one of the children
     * to the entity then return that path, including the root node since the path includes the root node itself.
     */
    if (root.children.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const child of root.children) {
            // Recursively call 'getPathToEntity' to find the path from the child to the entity. If there is a path then we are done searching
            // and can return the path.
            const path = getPathToEntity(child, entityId);
            if (path) {
                // If root node is NOT the dummy node we created to be the tree's root then include the root node
                if (root.entity.entityId !== ROOT_ENTITY_PARENT_ID && root.entity.entityId !== SEARCH_FILTER_ROOT_ENTITY_PARENT_ID) {
                    return [root.entity, ...path];
                }

                // Else only return the path excluding the root since it is just a dummy node
                return path;
            }
        }
    }

    return undefined;
};



/**
 * Given an array of entityClasses and an array of "tableViewSettingsPerEntityClasses", See if there is a entity classes specific settings
 * from the "tableViewSettingsPerEntityClasses" that we can use based on the entityClasses. For example,
 *      // The view is showing entities of these 2 classes
 *      entityClasses=["driver","vehicle"]
 *
 * And this is the config
 *      tableViewSettingsPerEntityClasses=[{
 *          entityClasses: ["user", "driver"],
 *          "settings": {...}
 *      }, {
 *          entityClasses: ["site", "driver", "vehicle"]
 *          "settings": {...}
 *      }]
 *
 * Because ALL the entity classes in "entityClasses" array matches the seconds item in "tableViewSettingsPerEntityClasses", we will use
 * the settings from the second item. The first item only matched 1 entity class "driver" so it's settings can't be used.
 */
const getTableViewSettings = (
    entityClasses: readonly string[] | undefined,
    tableViewSettingsPerEntityClasses: readonly [{
        readonly settings: EntitiesTableViewSettings;
        readonly entityClasses: readonly string[];
    }] | undefined,
): {
    readonly settings: EntitiesTableViewSettings;
    readonly entityClasses: readonly string[];
} | undefined => {
    if (entityClasses && entityClasses.length > 0 && tableViewSettingsPerEntityClasses) {
        return tableViewSettingsPerEntityClasses.find((customTableViewSettings) => {
            // If all the selected entity classes matches the customTableViewSettings.entityClasses array then we can use this
            // "customTableViewSettings"
            if (entityClasses.every((selectedEntityClass) => {
                return customTableViewSettings.entityClasses.includes(selectedEntityClass);
            })) {
                return true;
            }

            return false;
        });
    }

    return undefined;
};


/**
 * The set of fields from the entities listing table view that can be configured to be hidden
 */
export interface TableViewHiddenFields {
    readonly entityId?: boolean | undefined;
    readonly name?: boolean | undefined;
    readonly description?: boolean | undefined;
    readonly parentId?: boolean | undefined;
    readonly entityClass?: boolean | undefined;
    readonly homeId?: boolean | undefined;
    readonly icon?: boolean | undefined;
    readonly image?: boolean | undefined;
    readonly updatedAt?: boolean | undefined;
    readonly createdAt?: boolean | undefined;
}


/**
 * The default table view hidden fields.
 */
const DEFAULT_TABLE_HIDDEN_FIELDS: TableViewHiddenFields = Object.freeze({
    entityId : true,
    homeId   : true,
    parentId : true,
    createdAt: true,
    updatedAt: true,
    icon     : true,
});


/**
 * Interface for entities search and filter options
 */
interface EntitySearchFields {
    readonly homeIdFilter?: number | undefined;
    readonly entityClassesFilter?: readonly string[] | undefined;
    readonly attributesFilter: string | undefined;
    readonly parentFilter?: Entity | undefined;
    readonly recursive?: number | undefined;
    readonly sortField?: string | undefined;
    readonly sortOrder?: SortOrder | undefined;
}


/**
 * The option that can be customized by the parent container for viewType of type GRID and LIST. Because we use the EntityBasicViewUIComponent
 * for the Grid and List view, these options will just be a subset of the EntityBasicViewUIComponentCustomSettings interface.
 */
export type ListCardViewTypeOptions
    = Pick<EntityBasicViewUIComponentCustomSettings, 'showAttributes' | 'showImage' | 'showIcon' | 'viewDesign' | 'showEntityClass'>;


export interface EntitiesListingContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly homeId?: number | undefined;

    // To override the usageMode set in the uiState. Because this component can be used for other purposes and there are time when we need
    // this component to behave differently from the current usageMode. Therefore, we will allow the parent container to pass in a usageMode
    // to override the current usageMode set in the uiState.
    readonly usageMode?: UsageMode | undefined;

    // Whether we wants to show similar version of this component. We re-use this component in some drop down controls which don't require
    // all the features therefore we can use this flag to disable all the features that we don't need in minimalUI.
    readonly minimalUI?: boolean | undefined;

    readonly viewType?: EntitiesListingViewType | undefined;

    // Options for List and Card view types
    readonly listCardViewTypeOptions?: ListCardViewTypeOptions | undefined;

    // Whether or not to allow the user to create Entities from the container.
    readonly canCreateEntities?: boolean | undefined;

    // Whether or not to disable the Preview option. The default is FALSE unless this is specifically set to true.
    readonly disablePreviewOption?: boolean | undefined;

    // The set of entityIds that can't be selected. NOTE: disable an entity will also disable all its descendants as well.
    readonly disabledEntityIds?: readonly string[] | undefined;

    // Default pageSize (limit) use for pagination. If this is not provided, we will use internal default value
    readonly pageSize?: number | undefined;

    // OPTIONAL - To specify which entity to use as root. If not provided, we will use the project as root. This allow us to use this
    // container to display entities that belonged to a specific entity instead of always show entities belonged to a project or home.
    readonly parentId?: string | undefined;

    /**
     * By default, we will not use any filter at all initially until the user select a filter from using the control.
     * This option is for telling the component to use these filters initially when the component loads. The user can still change the filter
     * once the component is loaded. These filters are only use the FIRST time the component fetch data.
     */
    readonly presetFilters?: {
        readonly entityClasses?: readonly string[] | undefined;
    } | undefined;

    // By default, this container wil enable the homeId and entityClass filters. If the parent container wants to
    // disable any of these filters, it can specify it using this attribute. NOTE: if attribute 'homeId' is provided then this
    // homeId filters will always be disabled therefore there is no need to set homeIdFilter to false.
    readonly disabledFilters?: {
        readonly homeIdFilter?: boolean | undefined;
        readonly entityClassesFilter?: boolean | undefined;
        readonly parentFilter?: boolean | undefined;
        readonly attributesFilter?: boolean | undefined;
    };

    // OPTIONAL entity selected handler. The parent container can implement this handler and do what it needs to do when an entity is selected
    readonly onEntitySelected?: (entity: Entity)=> void;

    // Whether or not to display the control bar at the top of the page. Default is FALSE.
    readonly stickyControlBar?: boolean | undefined;

    readonly tableViewHiddenFields?: TableViewHiddenFields | undefined;

    // Whether or not to disable the user from expanding the entities tree. Sometimes we only want to show the entities at the top level
    // and don't want the user to expand the top level entities to drill down and view/select their children.
    readonly disableTreeExpansion?: boolean | undefined;
}



/**
 * This is a component that shows a list of entities.
 */
export const EntitiesListingContainer: React.FC<EntitiesListingContainerProps> = (props: EntitiesListingContainerProps) => {
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    const location = useLocation();
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const entitiesFeatureSettings = useMemo(() => {
        return getEntitiesFeatureSettings(project, loggedInAgent);
    }, [loggedInAgent, project]);
    const canEditEntity = props.canCreateEntities && projectAgent?.role === AgentRole.ADMIN;

    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const allProjectHomes = useGetAllProjectHomes(props.projectId);
    // Fetch entity object for the parentId if parentId is provided
    const parentEntity = useSelectEntity(props.projectId, props.parentId);
    const classes = useStyle();

    // Use the usageMode from props if it is specified. If not provided, use the usage mode from uiState
    const usageMode = props.usageMode ? props.usageMode : selectCurrentUsageMode(uiState);

    const [selectedEntityNode, setSelectedEntityNode] = useState<EntityNode | undefined>();

    // The state to keep track of the expanded nodes in the tree view
    const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);



    // State to keep track of the current selected viewType. If viewType is provided then use viewType. If not then check the
    // usageMode. If usageMode is VIEW_DATA, use TABLE else use TREE
    const [selectedViewType, setSelectedViewType] = useState<EntitiesListingViewType>(
        props.viewType ?? usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.defaultViewType ?? EntitiesListingViewType.TABLE
            : entitiesFeatureSettings?.viewsSettings?.manageView?.defaultViewType ?? EntitiesListingViewType.TREE,
    );

    // This useEffect is use for updating the "selectedViewType" when the usageMode changed after the component already initialized
    useEffect(() => {
        if (props.minimalUI) {
            // If "minimalUI" mode, always use the TREE view. This is the mode when we use the EntitiesListing component as a DropDown input
            // to have the user select an entity
            setSelectedViewType(EntitiesListingViewType.TREE);
        } else if (usageMode === UsageMode.VIEW_DATA) {
            setSelectedViewType(
                props.viewType ?? entitiesFeatureSettings?.viewsSettings?.dataView?.defaultViewType ?? EntitiesListingViewType.TABLE,
            );
        } else {
            // usageMode must be MANAGE_DATA so use the TREE view as default
            setSelectedViewType(
                props.viewType ?? entitiesFeatureSettings?.viewsSettings?.manageView?.defaultViewType ?? EntitiesListingViewType.TREE,
            );
        }
    }, [entitiesFeatureSettings?.viewsSettings?.dataView?.defaultViewType, entitiesFeatureSettings?.viewsSettings?.manageView?.defaultViewType,
        props.minimalUI, props.viewType, usageMode]);


    // State to keep track of the current selected sub root node
    const [subRootEntity, setSubRootEntity] = useState<Entity | undefined>();


    // Entity search/filter states
    const {
        searchParams, onOtherFieldsChange, onItemsPerPageChange, onNextPage, onPrevPage, onPage,
    } = useSearchParams<EntitySearchFields>({
        searchText  : '',
        itemsPerPage: props.pageSize ?? ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            homeIdFilter       : 0,
            entityClassesFilter: props.presetFilters?.entityClasses ?? undefined,
            attributesFilter   : undefined,
            parentFilter       : undefined,
            recursive          : undefined,
            sortField          : undefined,
            sortOrder          : undefined,
        },
    });


    // The list of attributes that user can filter by. This list of attributes is NOT fixed and can change whenever the user selected/unselect
    // entityClasses
    const [filterableAttributes, setFilterableAttributes] = useState<readonly EntityAttributeParam<EntityAttributeValueType>[] | undefined>();


    // There are a few places where we need to check whether the user selected some filter before we do something therefore we will
    // create a flag for this so we don't need to keep repeating it.
    const hasFilters = useMemo(() => {
        return searchParams.otherFields.homeIdFilter || searchParams.otherFields.entityClassesFilter
                || searchParams.otherFields.parentFilter || searchParams.otherFields.attributesFilter;
    }, [searchParams.otherFields.attributesFilter, searchParams.otherFields.entityClassesFilter, searchParams.otherFields.homeIdFilter,
        searchParams.otherFields.parentFilter]);


    // Whether or not the user selected a field to be sorted.
    const hasSortInfo = useMemo(() => {
        return searchParams.otherFields.sortField || searchParams.otherFields.sortOrder;
    }, [searchParams.otherFields.sortField, searchParams.otherFields.sortOrder]);


    // The root entity of the entity list we want to show. If parentId is provided, we will use this entity as the root and
    // show child entities of this parentId. If parentId is not provided then we will show project/home's entities
    const rootEntityId = props.parentId ?? ROOT_ENTITY_PARENT_ID;


    // A flag to prevent infinite loop of fetching data
    const isFetchingData = useRef(false);


    /**
     * Fetching entities require pagination params e.g. skip/limit. However, it is a more complicated than fetching normal list of data
     * which only have 1 skip/limit value, fetching entities has multiple skip values, 1 per branch. Therefore, we have to keep track of
     * the current pageNumber for EACH brach. We will keep track of the current pageNumber for each branch using this structure. e.g.
     *      {
     *          root: 3,            // we are currently on page 3 of the root.
     *          entityId1: 2,       // we are currently on page 2 of children of entityId1
     *          entityId5: 7,       // we are currently on page 7 of children of entityId5
     *          ...
     *      }
     */
    const [currentPageNumberByEntityId, setCurrentPageNumberByEntityId] = useState<{
        readonly [entityId: string]: number | undefined;
    }>(
        props.presetFilters
            ? {
                [SEARCH_FILTER_ROOT_ENTITY_PARENT_ID]: 0,       // If there is an initial filter to apply, use '' as root
            }
            : {
                [rootEntityId]: 0,
            },
    );


    /**
     * Given an parentId, SELECT the child entities for that parent from the data state based on the current page of the parent entity.
     * For example: If the current page of parent entity is 2 then we will look in the data state and get the first 3 pages of child entities
     * for this parent entity, page 0, page 1, and page 2.
     *
     * NOTE that this will NOT fetch data, it will only get the cached data from the data state which was fetched by another flow.
     */
    const selectFetchedChildEntities = useCallback((
        parentId: string | undefined,
        numberOfPagesToSelect: number,
        homeIdFilter: number | undefined = undefined,
        entityClassesFilter: readonly string[] | undefined = undefined,
        attributesFilter: string | undefined = undefined,
        recursive: number | undefined = undefined,
        sortFieldType: EntitySortFieldType.ATTRIBUTES | EntitySortFieldType.DESCENDANT_COUNTS | undefined = undefined,
        sortField: string | undefined = undefined,
        sortOrder: SortOrder | undefined = undefined,
    ): {
        readonly childEntities: readonly EntityNode[];
        readonly numberOfPagesFetched: number;
        readonly hasMoreChildren: boolean;
    } => {
        const childEntities: EntityNode[] = [];

        // Select child entities for the specified parentId 1 page at a time and combine them all in the allEntities array
        for (let pageNumber = 0; pageNumber < numberOfPagesToSelect; pageNumber += 1) {

            // SELECT the child entities from the data state for the current pageNumber
            const filters: UIFetchEntityFilters = {
                homeId         : homeIdFilter || undefined,
                pageNumber,
                pageSize       : searchParams.itemsPerPage,
                parentId,
                entityClasses  : entityClassesFilter?.join(','),
                attributesQuery: attributesFilter,
                recursive      : recursive !== undefined ? Boolean(recursive) : undefined,
                sortFieldType,
                sortField,
                sortOrder,
            };
            const childEntitiesPerPage = selectEntities(appDataState, props.projectId, filters);

            if (childEntitiesPerPage) {
                // For each child entity, add it to the childEntities array and and recursively get the child entities for this child entity
                childEntitiesPerPage.items.forEach((childEntity) => {
                    const results = selectFetchedChildEntities(
                        childEntity.entityId, (currentPageNumberByEntityId[childEntity.entityId] ?? 0) + 1, props.homeId, undefined, undefined, undefined,
                        sortFieldType, sortField, sortOrder,
                    );

                    // Create an EntityNode for the child entity
                    const childEntityNode = {
                        entity              : childEntity,
                        disabled            : props.disabledEntityIds?.includes(childEntity.entityId),
                        children            : results.childEntities,
                        numberOfPagesFetched: results.numberOfPagesFetched,
                        hasMoreChildren     : results.hasMoreChildren,
                    };

                    childEntities.push(childEntityNode);
                });
            } else {
                // We can't find the child entities for the current pageNumber from the data state. This is probably because the data
                // is not yet fetched. We will return whatever list of child entities we already selected from the data state.
                // And we will return some flag to indicate that not all child entities are fetched and there are more children to fetch.
                return {
                    childEntities,
                    numberOfPagesFetched: pageNumber,
                    hasMoreChildren     : true,
                };
            }
        }

        // If we get here, that means all the current pages of child entities for the 'parentId' is fetched
        // However, it does not mean there is no more children to fetch. It just mean for example, that if currentPage is 3 then
        // we have fetched all 3 pages of child entities.
        return {
            childEntities,
            numberOfPagesFetched: numberOfPagesToSelect,
            // We don't exactly know if the parent entity has anymore children. We can only guess that if the number of children equal
            // the number of pages * pageSize then the parent probably has more children.
            hasMoreChildren     : childEntities.length === (numberOfPagesToSelect) * searchParams.itemsPerPage,
        };
    }, [appDataState, currentPageNumberByEntityId, props.disabledEntityIds, props.homeId, props.projectId, searchParams.itemsPerPage]);



    /**
     * Create a dummy/fake ROOT entity node for the rootEntityId. We need a root entity because entities are like a tree and we will represent
     * them like a tree. Under this root entity node will be the REAL entities that we wanted to display.
     */
    const rootEntityNode = useMemo((): EntityNode => {
        if (hasFilters) {
            // If homeId is provided by the parent container then ALWAYS use this homeId, there shouldn't be an option to choose a different
            // homeId. If there is no homeId provided by the container and the homeIdFilter is selected then we will use the filter's homeId
            const homeIdFilter = !props.homeId && searchParams.otherFields.homeIdFilter ? searchParams.otherFields.homeIdFilter : props.homeId;
            const entityClassesFilter = searchParams.otherFields.entityClassesFilter;
            const attributesFilter = searchParams.otherFields.attributesFilter;
            const parentFilter = searchParams.otherFields.parentFilter;
            const recursive = searchParams.otherFields.recursive;
            const { sortFieldType, sortField } = splitEntitySortFieldInfo(searchParams.otherFields.sortField);
            const sortOrder = searchParams.otherFields.sortOrder;


            // Select childEntities using SEARCH_FILTER_ROOT_ENTITY_PARENT_ID as parentId because we want all entities that matches the filters
            // doesn't matter which level they are in.
            const { childEntities, numberOfPagesFetched, hasMoreChildren } = selectFetchedChildEntities(
                parentFilter?.entityId ?? SEARCH_FILTER_ROOT_ENTITY_PARENT_ID,
                (currentPageNumberByEntityId[SEARCH_FILTER_ROOT_ENTITY_PARENT_ID] ?? 0) + 1,
                homeIdFilter, entityClassesFilter, attributesFilter, recursive, sortFieldType, sortField, sortOrder,
            );

            return {
                entity  : createDummyEntity(SEARCH_FILTER_ROOT_ENTITY_PARENT_ID),
                children: childEntities,
                hasMoreChildren,
                numberOfPagesFetched,
            };
        }

        // Start by fetching childEntities for the root. 'selectFetchedChildEntitiesForOneParent' will recursively fetch grandchildren entities
        // for the returned childEntities.
        const { sortFieldType, sortField } = splitEntitySortFieldInfo(searchParams.otherFields.sortField);
        const sortOrder = searchParams.otherFields.sortOrder;

        const { childEntities, numberOfPagesFetched, hasMoreChildren } = selectFetchedChildEntities(
            rootEntityId, (currentPageNumberByEntityId[rootEntityId] ?? 0) + 1, props.homeId, undefined, undefined, undefined,
            sortFieldType, sortField, sortOrder,
        );

        return {
            // If props.parentId is provided and parentEntity is loaded, use that entity object. Else, create a dummy entity object
            entity  : props.parentId && parentEntity ? parentEntity : createDummyEntity(rootEntityId),
            children: childEntities,
            hasMoreChildren,
            numberOfPagesFetched,
        };

    }, [hasFilters, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder, searchParams.otherFields.homeIdFilter,
        searchParams.otherFields.entityClassesFilter, searchParams.otherFields.parentFilter, searchParams.otherFields.recursive,
        searchParams.otherFields.attributesFilter, selectFetchedChildEntities, rootEntityId, currentPageNumberByEntityId, props.homeId,
        props.parentId, parentEntity]);


    /**
     * Fetch the next page of child entities for the specified parentId. This will check to see which page we are current on
     * for the specified parentId and then call the fetchChildEntities to fetch the next page. And this will also increment
     * the current page by 1 and store this value in the currentPageNumberByEntityId.
     */
    const fetchNextPageOfChildEntities = useCallback((parentId: string) => {
        // Get the currentPage for the specified parentId. If it is not set, set it to -1 so that when
        // it will be 0 when we increment it by one
        const currentPageNumber = currentPageNumberByEntityId[parentId] ?? -1;
        const nextPageNumber = currentPageNumber + 1;

        // We only need to increment the page counter. One of the useEffects will take care of fetching data when this page number changed
        setCurrentPageNumberByEntityId({
            ...currentPageNumberByEntityId,
            [parentId]: nextPageNumber,
        });
    }, [currentPageNumberByEntityId]);



    /**
     * Helper function to fetch child entities for 1 parentId for N number of pages.
     */
    const fetchChildEntitiesForOneParent = useCallback((
        parentId: string, numberOfPagesToFetch: number, homeIdFilter: number | undefined, entityClassesFilter: readonly string[] | undefined,
        attributesFilter: string | undefined,
        recursive: number | undefined,
        sortFieldType?: EntitySortFieldType.ATTRIBUTES | EntitySortFieldType.DESCENDANT_COUNTS | undefined,
        sortField?: string,
        sortOrder?: SortOrder,
    ) => {
        const fetchEntitiesAction: Promise<void>[] = [];

        for (let currentPage = 0; currentPage < numberOfPagesToFetch; currentPage += 1) {
            // Create a params used for fetching/selecting data
            const fetchDataParams: UIFetchEntityFilters = {
                homeId         : homeIdFilter || undefined,
                pageNumber     : currentPage,
                pageSize       : searchParams.itemsPerPage,
                parentId,
                entityClasses  : entityClassesFilter?.join(','),
                attributesQuery: attributesFilter,
                recursive      : recursive !== undefined ? Boolean(recursive) : undefined,
                sortFieldType,
                sortField,
                sortOrder,
            };

            // Check if we already have data for the currentPage first. If we do, don't need to dispatch event to fetch data
            const childEntities = selectEntities(appDataState, props.projectId, fetchDataParams);
            if (!childEntities) {
                fetchEntitiesAction.push(appDataDispatch(fetchEntities(props.projectId, fetchDataParams), false));
            }
        }
        return fetchEntitiesAction;
    }, [appDataDispatch, appDataState, props.projectId, searchParams.itemsPerPage]);



    /**
     * This function will fetch child entities for all entities in currentPageNumberByEntityId. It will fetch data for all pages
     * starting from page 0 up to the current page in currentPageNumberByEntityId. For example: If the currentPageNumberByEntityId is
     *      {
     *          root: 2,
     *          entity1: 3,
     *          entity2: 1
     *      }
     *
     * This will load 3 pages of entities for the root (page 0, 1, and 2), 4 pages of entities for entity1, and 2 pages for entity2.
     */
    const fetchChildEntitiesForAllParents = useCallback(async (
        rootParentId: string, homeIdFilter?: number | undefined, entityClassesFilter?: readonly string[] | undefined,
        attributesFilter?: string | undefined,
        recursive?: number | undefined,
        sortFieldType?: EntitySortFieldType.ATTRIBUTES | EntitySortFieldType.DESCENDANT_COUNTS | undefined,
        sortField?: string,
        sortOrder?: SortOrder,
    ) => {
        const entityIds = Object.keys(currentPageNumberByEntityId);
        const fetchEntitiesActions: Promise<void>[] = [];
        entityIds.forEach((entityId) => {
            // Find the currentPage we are on and the fetch data for each page up until the currentPage for any page that we don't have data for
            const currentPage = currentPageNumberByEntityId[entityId];
            if (currentPage !== undefined) {
                // call helper function to create actions to fetch childEntities for the current entityId and for the numberOfPages
                // and then combine the actions into the fetchEntitiesAction. We will resolve them all at the same time
                if (entityId === rootParentId) {
                    // For FIRST level, root, we we will use filter to fetch entities
                    fetchChildEntitiesForOneParent(
                        entityId, currentPage + 1, homeIdFilter, entityClassesFilter, attributesFilter, recursive, sortFieldType, sortField, sortOrder,
                    ).forEach((action) => {
                        fetchEntitiesActions.push(action);
                    });
                } else {
                    // For other level that is not root, we don't need to use filter
                    fetchChildEntitiesForOneParent(
                        entityId, currentPage + 1, props.homeId, undefined, undefined, undefined, sortFieldType, sortField, sortOrder,
                    ).forEach((action) => {
                        fetchEntitiesActions.push(action);
                    });
                }
            }
        });
        await Promise.allSettled(fetchEntitiesActions);
    }, [currentPageNumberByEntityId, fetchChildEntitiesForOneParent, props.homeId]);



    /**
     * This useEffect is used for fetching child entities for all entities in the currentPageNumberByEntityId.
     */
    useEffect(() => {
        if (hasFilters || hasSortInfo) {
            return;
        }
        if (!isFetchingData.current) {
            isFetchingData.current = true;
            fetchChildEntitiesForAllParents(rootEntityId, props.homeId).then(() => {
                isFetchingData.current = false;
            }).catch(() => {
                // ignore error because appDataDispatch already handled showing the error
                isFetchingData.current = false;
            });
        }
    }, [fetchChildEntitiesForAllParents, hasFilters, hasSortInfo, isFetchingData, props.homeId, rootEntityId, rootEntityNode]);


    /**
     * This useEffect is used for fetching entities with filter options
     */
    useEffect(() => {
        if (!hasFilters && !hasSortInfo) {
            return;
        }

        if (!isFetchingData.current) {
            isFetchingData.current = true;
            // If homeId is provided by the parent container then ALWAYS use this homeId, there shouldn't be an option to choose a different
            // homeId. If there is no homeId provided by the container and the homeIdFilter is selected then we will use the selected homeIdFilter
            const homeIdFilter = !props.homeId && searchParams.otherFields.homeIdFilter
                ? searchParams.otherFields.homeIdFilter : props.homeId;

            const entityClassesFilter = searchParams.otherFields.entityClassesFilter;
            const attributeFilters = searchParams.otherFields.attributesFilter;
            const parentFilter = searchParams.otherFields.parentFilter;
            const recursive = searchParams.otherFields.recursive;
            const { sortFieldType, sortField } = splitEntitySortFieldInfo(searchParams.otherFields.sortField);
            const sortOrder = searchParams.otherFields.sortOrder;

            fetchChildEntitiesForAllParents(
                parentFilter?.entityId ?? SEARCH_FILTER_ROOT_ENTITY_PARENT_ID, homeIdFilter, entityClassesFilter, attributeFilters, recursive,
                sortFieldType, sortField, sortOrder,
            ).then(() => {
                isFetchingData.current = false;
            }).catch(() => {
                // ignore error because appDataDispatch already handled showing the error
                isFetchingData.current = false;
            });
        }
    }, [fetchChildEntitiesForAllParents, hasFilters, hasSortInfo, props.homeId, searchParams.otherFields.parentFilter,
        searchParams.otherFields.attributesFilter, searchParams.otherFields.entityClassesFilter, searchParams.otherFields.homeIdFilter,
        searchParams.otherFields.recursive, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder]);



    /**
     * The EntitiesListingComp is a DUMB component which does not have any logic, it can only display whatever data we pass to it.
     * Therefore, we need to localize the entity nodes and translate whatever we need to translate before we pass the entity nodes
     * to the EntitiesListingComp to display.
     */
    const localizedRootEntity = useMemo((): ReadonlyNonEmptyArray<EntitiesListingDataItem> | undefined => {
        if (rootEntityNode && entityClasses) {
            // Call localizeSingleEntity to localize the root node. This function will recursively localize all the child nodes
            // and grandchild nodes and so on.
            const result = localizeSingleEntity(rootEntityNode, props.projectId, entityClasses, appDataState, appDataDispatch, trans);

            // Because the EntitiesListingComp's listData requires an Array of item therefore we will create an array
            // of just 1 node, the root node
            return [result];
        }
        return undefined;
    }, [appDataDispatch, appDataState, entityClasses, props.projectId, rootEntityNode, trans]);



    /**
     * On Preview button clicked, open the SlideOutPanel to show the preview of the selected Entity
     */
    const onPreviewEntityNodeClicked = useCallback((selectedNode: EntityNode | undefined) => {
        const action: EntityPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ENTITY_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    entityId : selectedNode?.entity.entityId,
                },
                enableCreateChildren: true,
                enableDeleteEntity  : true,
            },
        };
        uiDispatch(showSlideOutPanel(action));
    }, [props.projectId, uiDispatch]);



    /**
     * On entity node selected, we will check if we have ever fetched children for this entity. If we have not then we will try to fetch
     * children for this node. If this node already have children then we don't need to fetch children for the selected node. To fetch
     * more children for the selected node, the user MUST use the "..." button.
     */
    const onEntityNodeSelected = useCallback((selectedNode: EntityNode) => {
        if (selectedNode.entity.entityId.includes(LOAD_MORE_NODE_ID_PREFIX)) {
            // if the selected entity node's entityId contains the string LOAD_MORE_NODE_ID_PREFIX, we know it is a dummy entity used
            // for LOAD_MORE button. We need to extract the parent entityId from this dummy entity's Id and then call load data for
            // the parent entity
            const parentId = getEntityIdFromLoadMoreEntityNode(selectedNode.entity.entityId);
            fetchNextPageOfChildEntities(parentId);
        } else {
            // If the user is in VIEW_DATA mode, selecting an entity will automatically take the user to the entity details page
            if (!props.minimalUI && usageMode === UsageMode.VIEW_DATA) {
                RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, selectedNode.entity.entityId);
                return;
            }

            // If we have not fetch the selected entity's children at least once, we will fetch them. But if we already
            // fetched the children for the selected entity then don't do it EVEN IF this entity has more children to load.
            // The user will have to click the "Load more" button to load more child entities.
            if (selectedNode.children.length === 0 && selectedNode.numberOfPagesFetched === 0) {
                fetchNextPageOfChildEntities(selectedNode.entity.entityId);
            }

            // Update the local selectedNode state and also notify the parent container an entity is selected.
            setSelectedEntityNode(selectedNode);
            if (props.onEntitySelected) {
                props.onEntitySelected(selectedNode.entity);
            }

            // Automatically show Preview panel for the selected entity
            if (!props.disablePreviewOption) {
                onPreviewEntityNodeClicked(selectedNode);
            }
        }
    }, [fetchNextPageOfChildEntities, onPreviewEntityNodeClicked, props, usageMode]);



    // Create a component for the EntitiesListingComp to display when there is no data
    const noDataMessageComp = useMemo(() => {
        return (
            <GenericMessageBox
                title={trans('generic.no_data.title', {
                    term: capitalizeAll(trans('terms.entity', { count: 2 })),
                })}
            />
        );
    }, [trans]);


    // Create a component for the EntitiesListingComp to display when there is data but data is empty
    const emptyDataMessageComp = useMemo(() => {
        return (
            <GenericMessageBox
                title={trans('generic.no_data.title', {
                    term: capitalizeAll(trans('terms.entity', { count: 2 })),
                })}
            />
        );
    }, [trans]);



    /**
     * On filter change handler.
     * For now, we only care about entityClassesFilter change event because when an entity class is selected/unselected, we will
     * need to update the list of attributes the user can search by
     */
    const onFilterChanged = useCallback((filters: {
        readonly homeIdFilter?: number | undefined;
        readonly entityClassesFilter?: readonly string[] | undefined;
        readonly attributesFilter?: AttributesQuery | undefined;
    }) => {
        if (filters.entityClassesFilter) {
            const filterableAttributes = getFilterableAttributes(entityClasses, filters.entityClassesFilter);
            setFilterableAttributes(filterableAttributes);
        }
    }, [entityClasses]);


    /**
     * This useEffect is used for initializing the filterableAttributes once when entityClasses is loaded.
     */
    useEffect(() => {
        if (entityClasses) {
            const filterableAttributes = getFilterableAttributes(entityClasses);
            setFilterableAttributes(filterableAttributes);
        }
    }, [entityClasses]);


    /**
     * This useEffect is used for updating the filterableAttributes when the container passed in a different "presetFilters.entityClasses"
     */
    useEffect(() => {
        if (props.presetFilters?.entityClasses) {
            const filterableAttributes = getFilterableAttributes(entityClasses, props.presetFilters.entityClasses);
            setFilterableAttributes(filterableAttributes);
        }
    }, [entityClasses, props.presetFilters?.entityClasses]);


    // Create a list of Attribute options that the user can select from when they to select an attribute to filter
    // This need to be updated each time the 'filterableAttributes' changed.
    const filterableAttributeOptions = useMemo(() => {
        return (filterableAttributes || [])?.map((attributeConfig) => {
            return {
                label: attributeConfig.label,
                value: attributeConfig,
            };
        });
    }, [filterableAttributes]);



    /**
     * On Apply search and filter button clicked, we will update the searchParam. The useEffect will detect changes to the searchParams
     * and will fetch data accordingly.
     */
    const onApplySearchAndFilters = useCallback((filters: {
        readonly homeIdFilter?: number | undefined;
        readonly entityClassesFilter?: readonly string[] | undefined;
        readonly parentFilter?: {
            readonly entity: Entity | undefined;
            readonly recursive: number | undefined;
        } | undefined;
        readonly attributesFilter?: AttributesQuery | undefined;
    }) => {
        // Attributes query is an object containing attribute conditions. We need to convert it to strings e.g.
        // AND(name==="joe",age>=18,age<=100)
        const attributesQueryString = filters.attributesFilter ? convertAttributesQueryToString(filters.attributesFilter) : undefined;

        // If at least 1 of the filters changed then update the 'searchParams'
        if (
            filters.homeIdFilter !== searchParams.otherFields.homeIdFilter
            || filters.entityClassesFilter !== searchParams.otherFields.entityClassesFilter
            || filters.parentFilter?.entity !== searchParams.otherFields.parentFilter
            || filters.parentFilter?.recursive !== searchParams.otherFields.recursive
            || attributesQueryString !== searchParams.otherFields.attributesFilter
        ) {
            // Filter changed, we need to reset the object which we used for keeping track of the current pages for all entities
            if (filters.homeIdFilter || (filters.entityClassesFilter && filters.entityClassesFilter?.length > 0)
                || (filters.attributesFilter && filters.attributesFilter.conditions.length > 0)
                || (filters.parentFilter && filters.parentFilter.entity !== undefined)
                || (filters.parentFilter && filters.parentFilter.recursive !== undefined)
            ) {
                setCurrentPageNumberByEntityId({ [filters.parentFilter?.entity?.entityId ?? SEARCH_FILTER_ROOT_ENTITY_PARENT_ID]: 0 });
            } else {
                setCurrentPageNumberByEntityId({ [rootEntityId]: 0 });
            }

            // When the user apply search input, we need to clear the current selected subroot entity because the search result will
            // be the new root.
            setSubRootEntity(undefined);

            // When sub root changed, need to reset the table current page
            onPage(0);

            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<EntitySearchFields>) => {
                // if filters.homeId is 'undefined', it mean the filter WAS NOT enabled or selected so we don't need to change the searchParams.
                // It does not mean the user doesn't want to filter by homeId and want to see all entities in a project.
                if (filters.homeIdFilter !== undefined) {
                    draft.homeIdFilter = filters.homeIdFilter;
                }
                if (filters.entityClassesFilter !== undefined) {
                    // if new filters is empty, set it to 'undefined' instead of empty array
                    draft.entityClassesFilter = castDraft(filters.entityClassesFilter.length > 0 ? filters.entityClassesFilter : undefined);
                }
                if (filters.parentFilter !== undefined) {
                    draft.parentFilter = castDraft(filters.parentFilter.entity);
                    draft.recursive = filters.parentFilter.recursive;
                }
                if (filters.attributesFilter !== undefined) {
                    draft.attributesFilter = attributesQueryString;
                }
            }));
        }
    }, [onOtherFieldsChange, onPage, rootEntityId, searchParams.otherFields]);



    /**
     * Whenever the subRootEntity changed, we need to get the path from the root node to that subRootEntity so that we can
     * build the breadcrumbs
     */
    const pathToSubRootEntity = useMemo((): readonly Entity[] => {
        if (
            subRootEntity && subRootEntity.entityId !== ROOT_ENTITY_PARENT_ID
            && subRootEntity.entityId !== SEARCH_FILTER_ROOT_ENTITY_PARENT_ID
        ) {
            const path = getPathToEntity(rootEntityNode, subRootEntity.entityId);
            return path ?? [];
        }
        return [];
    }, [subRootEntity, rootEntityNode]);


    /**
     * Create the config for the searchAndFilters settings to be passed to EntitiesListingComp.
     *
     * homeIdFilter - We will only enable this option if props.homeId is NOT provided. If props.homeId is provided that means the
     * user wants to use this container to display entities belonged to a specific home. Therefore, we can't allow the user to
     * choose a different home. Also, we will only show this homeIdFilter if 'props.disabledFilters.homeIdFilter' is not specifically set to true.
     *
     * entityClassesFilter - We will always show this option UNLESS 'props.disabledFilters.entityClassesFilter' is specifically set to true
     */
    const entitiesListingCompFilters = useMemo((): EntitiesListingControlBarProps['searchAndFilterSettings'] => {
        return {
            homeIdFilterSettings: !props.homeId && props.disabledFilters?.homeIdFilter !== true ? {
                currentValue: searchParams.otherFields.homeIdFilter,
                label       : capitalizeFirst(trans('entity.filter.home_id.label')),
                // Create a list of Home options from the project homes data. Also add option "None" first.
                options     : [{
                    label: capitalizeAll(trans('label.none')),
                    value: 0,
                }, ...((allProjectHomes || [])?.map((home) => {
                    return {
                        label: home.name ?? home.id,
                        value: home.id,
                    };
                }))],
            } : undefined,
            entityClassesFilterSettings: props.disabledFilters?.entityClassesFilter !== true ? {
                currentValue: searchParams.otherFields.entityClassesFilter ?? [],
                label       : capitalizeFirst(trans('entity.filter.entity_class.label')),
                // Create a list of entity class options from the entityClasses data
                options     : (entityClasses || [])?.map((entityClass) => {
                    return {
                        label: entityClass.displaySettings.name,
                        value: entityClass.entityClass,
                    };
                }),
            } : undefined,
            parentFilterSettings: props.disabledFilters?.parentFilter !== true ? {
                entity: {
                    currentValue                 : undefined,
                    label                        : capitalizeFirst(trans('entity.filter.parent.label')),
                    entitiesListingContainerProps: {
                        projectId           : props.projectId,
                        homeId              : searchParams.otherFields.homeIdFilter,
                        minimalUI           : true,
                        disablePreviewOption: false,
                    },
                    texts: {
                        clearButtonLabel : capitalizeAll(trans('label.entire_project')),
                        cancelButtonLabel: capitalizeAll(trans('label.cancel')),
                        selectButtonLabel: capitalizeAll(trans('label.select')),
                        emptyValueDisplay: capitalizeAll(trans('label.entire_project')),
                    },
                },
                recursive: {
                    currentValue: undefined,
                    label       : capitalizeFirst(trans('entity.filter.recursive.label')),
                    options     : [{
                        label: capitalizeAll(trans('entity.filter.recursive.option.no')),
                        value: 0,
                    }, {
                        label: capitalizeAll(trans('entity.filter.recursive.option.yes')),
                        value: 1,
                    }],
                },
            } : undefined,
            attributesFilterSettings: props.disabledFilters?.attributesFilter !== true ? {
                currentValue    : undefined,
                label           : capitalizeFirst(trans('entity.filter.attribute.label')),
                attributeOptions: filterableAttributeOptions,

                // Create the drop down options for the operator input. We will prepare the options for ALL type of attributes
                operatorOptions: {
                    [EntityAttributeType.NUMBER]: SUPPORTED_OPERATORS_BY_ATTRIBUTE_TYPE[EntityAttributeType.NUMBER].map((operator) => {
                        return {
                            label: capitalizeAll(trans(getNumericAttributeCompareOperatorDisplayName(operator))),
                            value: operator,
                        };
                    }),
                    [EntityAttributeType.BOOLEAN]: SUPPORTED_OPERATORS_BY_ATTRIBUTE_TYPE[EntityAttributeType.BOOLEAN].map((operator) => {
                        return {
                            label: capitalizeAll(trans(getNumericAttributeCompareOperatorDisplayName(operator))),
                            value: operator,
                        };
                    }),
                    [EntityAttributeType.STRING]: SUPPORTED_OPERATORS_BY_ATTRIBUTE_TYPE[EntityAttributeType.STRING].map((operator) => {
                        return {
                            label: capitalizeAll(trans(getStringAttributeCompareOperatorDisplayName(operator))),
                            value: operator,
                        };
                    }),
                },
            } : undefined,
            texts: {
                applyFiltersButtonLabel         : capitalizeAll(trans('label.search')),
                closeFilterButtonLabel          : capitalizeAll(trans('label.close')),
                emptyInputMessage               : capitalizeAll(trans('label.none')),
                attributeNameLabel              : capitalizeAll(trans('entity.filter.attribute.label')),
                attributeValueLabel             : capitalizeAll(trans('label.value')),
                orButtonLabel                   : capitalizeAll(trans('label.or')),
                addAttributeFilterButtonLabel   : capitalizeAll(trans('entity.filter.add.label')),
                removeAttributeFilterButtonLabel: capitalizeAll(trans('label.remove')),
                booleanTrueValue                : capitalizeAll(trans('entity.attribute.boolean_value.true')),
                booleanFalseValue               : capitalizeAll(trans('entity.attribute.boolean_value.false')),
                conjunctionInputLabel           : capitalizeAll(trans('entity.filter.attribute_conjunction.label')),
                andConjunctionValue             : trans('entity.filter.attribute_conjunction.and'),
                orConjunctionValue              : trans('entity.filter.attribute_conjunction.or'),
                attributeTypeLabel              : {
                    [EntityAttributeType.BOOLEAN]: capitalizeAll(trans('entity.attribute.type.boolean')),
                    [EntityAttributeType.NUMBER] : capitalizeAll(trans('entity.attribute.type.number')),
                    [EntityAttributeType.STRING] : capitalizeAll(trans('entity.attribute.type.string')),
                },
            },
            closeOnApply: true,
            onFilterChanged,
            onApplySearchAndFilters,
        };
    }, [allProjectHomes, entityClasses, filterableAttributeOptions, onApplySearchAndFilters, onFilterChanged, props.disabledFilters?.parentFilter,
        props.disabledFilters?.attributesFilter, props.disabledFilters?.entityClassesFilter, props.disabledFilters?.homeIdFilter, props.homeId,
        props.projectId, searchParams.otherFields.entityClassesFilter, searchParams.otherFields.homeIdFilter, trans]);


    /**
     * Create a cache the settings for the Tree View. This will be passed to the EntitiesListingComp later
     */
    const entitiesListingTreeViewSettings = useMemo((): EntitiesListingCompProps['treeViewSettings'] => {
        return {
            hideExpandCollapseIcon  : props.disableTreeExpansion,
            expandedNodeIds,
            onExpandedNodeIdsChanged: !props.disableTreeExpansion ? setExpandedNodeIds : undefined,
        };
    }, [expandedNodeIds, props.disableTreeExpansion]);


    /**
     * Create a cache the settings for the cardView. This will be passed to the EntitiesListingComp later
     */
    const entitiesListingCompCardViewSettings = useMemo((): EntitiesListingCompProps['cardViewSettings'] => {
        return {
            cardCompSettings: {
                viewDesign: selectedViewType === EntitiesListingViewType.LIST
                    ? EntityBasicViewDesign.DESIGN_1 : EntityBasicViewDesign.DEFAULT,
                showNoImageMessage   : true,
                showAttributes       : true,
                showEntityClass      : true,
                excludeAttributeTypes: [EntityAttributeType.ARRAY, EntityAttributeType.OBJECT],

                // Override the default options with the customized options
                ...props.listCardViewTypeOptions,
            },
        };
    }, [props.listCardViewTypeOptions, selectedViewType]);



    /**
     * Compile a list of entity classes' attributes to be included in the Table view. We only need to do this for TABLE view AND only if
     * the project's tableViewSettings.showAttributesSettings is configured.
     */
    const attributesToDisplay = useMemo((): EntityAttributeParam<EntityAttributeValueType>[] => {

        // Get the list of selected entity classes in the filter.
        const selectedEntityClasses = searchParams.otherFields.entityClassesFilter;

        // Get the table view settings per entity classes from the project's UI Settings
        const tableViewSettingsPerEntityClasses = usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.tableViewPerEntityClasses
            : entitiesFeatureSettings?.viewsSettings?.manageView?.tableViewPerEntityClasses;

        // Get the project's global table view settings from the project's UI Settings
        const projectTableViewSettings = usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.tableView
            : entitiesFeatureSettings?.viewsSettings?.manageView?.tableView;

        // Get the tableViewSettings from the "tableViewSettingsPerEntityClasses" that is configured for the selectedEntityClasses
        const perEntityClassesSettings = getTableViewSettings(selectedEntityClasses, tableViewSettingsPerEntityClasses);


        // Now pick the showAttributesSettings from either the "perEntityClassesSettings" or the
        // global project settings. We will try to use the specific settings for entity classes first if possible
        const showAttributesSettings = perEntityClassesSettings?.settings.showAttributesSettings
                                        ?? projectTableViewSettings?.showAttributesSettings;


        if (selectedViewType === EntitiesListingViewType.TABLE && showAttributesSettings) {

            // Get all the entities class for the entities that are being displayed. We will display the attributes for these entity classes
            const entityClassesToDisplay: Set<EntityClass> = new Set<EntityClass>();

            if (selectedEntityClasses && selectedEntityClasses.length > 0) {
                // If there are some entity classes selected in the filters then use those entityClasses
                entityClasses?.forEach((entityClass) => {
                    if (selectedEntityClasses.includes(entityClass.entityClass)) {
                        entityClassesToDisplay.add(entityClass);
                    }
                });
            } else {
                // If no entity classes is selected in the filter then use the entity classes of the currently displayed entities. So if the current
                // view is showing entities of 2 entity classes e.g. Driver and Vehicle, this will include the Driver and Vehicle entity classes.
                // If the user go to the next page and show more entities from other entity classes then will will add the new entity classes.

                // First, find the root node that the user is looking at. We will only need to get entity classes for the entities that are
                // being displayed on the current page, not all entities in the tree.
                const root = subRootEntity ? findEntityNode(rootEntityNode, subRootEntity.entityId) : rootEntityNode;

                root?.children.forEach((child) => {
                    const entityClass = entityClasses?.find((entityClass) => {
                        return entityClass.entityClass === child.entity.entityClass;
                    });
                    if (entityClass) {
                        entityClassesToDisplay.add(entityClass);
                    }
                });
            }

            // Sort the entityClassesToDisplay so that when we compile the list of attributes from all the entity classes, the list stay the same
            // order doesn't matter the order of the entities
            const sortedEntityClassesToDisplay = Array.from(entityClassesToDisplay).sort((entityClass1, entityClass2) => {
                if (entityClass1.entityClass > entityClass2.entityClass) {
                    return 1;
                }
                if (entityClass1.entityClass < entityClass2.entityClass) {
                    return -1;
                }
                return 0;
            });

            // Now get all the attributes we need to display for those sortedEntityClassesToDisplay
            const attributes: EntityAttributeParam<EntityAttributeValueType>[] = [];

            // For each entity class, get the entity class' attributes and check one by one attribute to see which attributes we need to
            // show in the table. We will filter out attributes based on the project's includesAttributes and excludesAttributes settings.
            sortedEntityClassesToDisplay.forEach((entityClass) => {
                const includesAttributes = showAttributesSettings.includeAttributes;
                const excludeAttributes = showAttributesSettings.excludeAttributes;

                entityClass.attributeSchema?.forEach((attribute) => {
                    // If the "includesAttributes" array is configured but the "attribute" IS NOT one of the attributes in "includesAttributes"
                    // array then don't add it to the list
                    if (includesAttributes && !includesAttributes.includes(attribute.key)) {
                        return;
                    }
                    // If the "excludeAttributes" array is configured and the "attribute" IS one of the attributes in the excludeAttributes
                    // array then don't add it to the list
                    if (excludeAttributes && excludeAttributes.includes(attribute.key)) {
                        return;
                    }
                    // The "attribute" passed all the check so it should be include in the result.
                    attributes.push(attribute);
                });
            });

            return attributes;
        }
        return [];
    }, [entitiesFeatureSettings?.viewsSettings?.dataView?.tableView, entitiesFeatureSettings?.viewsSettings?.dataView?.tableViewPerEntityClasses,
        entitiesFeatureSettings?.viewsSettings?.manageView?.tableView, entitiesFeatureSettings?.viewsSettings?.manageView?.tableViewPerEntityClasses,
        entityClasses, rootEntityNode, searchParams.otherFields.entityClassesFilter, selectedViewType, subRootEntity, usageMode]);


    /**
     * Compile a list of descendant names to be included in the Table view. We only need to do this for TABLE view AND only if
     * the project's tableViewSettings.showDescendantCountsSettings is configured.
     */
    const descendantCountsToDisplay = useMemo((): readonly string[] => {

        // Get the list of selected entity classes in the filter.
        const selectedEntityClasses = searchParams.otherFields.entityClassesFilter;

        // Get the table view settings per entity classes from the project's UI Settings
        const tableViewSettingsPerEntityClasses = usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.tableViewPerEntityClasses
            : entitiesFeatureSettings?.viewsSettings?.manageView?.tableViewPerEntityClasses;

        // Get the project's global table view settings from the project's UI Settings
        const projectTableViewSettings = usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.tableView
            : entitiesFeatureSettings?.viewsSettings?.manageView?.tableView;

        // Get the tableViewSettings from the "tableViewSettingsPerEntityClasses" that is configured for the selectedEntityClasses
        const perEntityClassesSettings = getTableViewSettings(selectedEntityClasses, tableViewSettingsPerEntityClasses);


        // Now pick the showDescendantCountsSettings from either the "perEntityClassesSettings" or the
        // global project settings. We will try to use the specific settings for entity classes first if possible
        const showDescendantCountsSettings = perEntityClassesSettings?.settings.showDescendantCountsSettings
                                        ?? projectTableViewSettings?.showDescendantCountsSettings;


        if (selectedViewType === EntitiesListingViewType.TABLE && showDescendantCountsSettings?.includeDescendantCounts) {
            return showDescendantCountsSettings.includeDescendantCounts;
        }
        return [];
    }, [entitiesFeatureSettings?.viewsSettings?.dataView?.tableView, entitiesFeatureSettings?.viewsSettings?.dataView?.tableViewPerEntityClasses,
        entitiesFeatureSettings?.viewsSettings?.manageView?.tableView, entitiesFeatureSettings?.viewsSettings?.manageView?.tableViewPerEntityClasses,
        searchParams.otherFields.entityClassesFilter, selectedViewType, usageMode]);


    /**
     * On sort column clicked, we will update the sortField and sortOrder based on the column the user clicked.
     */
    const onSortColumnClicked = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft) => {
                if (field.dataItemProp) {
                    if (draft.sortField === field.dataItemProp) {
                        // this column is currently being sorted so don't need to change the "sortField"
                        // But we need to change/toggle the sort order
                        if (draft.sortOrder === SortOrder.ASC) {
                            draft.sortOrder = SortOrder.DESC;
                        } else {
                            draft.sortOrder = SortOrder.ASC;
                        }
                    } else {
                        // This column is not the current column being sorted, change the "sortField"
                        draft.sortField = field.dataItemProp;
                        if (!draft.sortOrder) {
                            draft.sortOrder = SortOrder.ASC;
                        }
                    }
                }
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);


    /**
     * Create a cache the FIELDS settings for the Table view. This will be passed to the EntitiesListingComp later
     */
    const entitiesListingCompTableViewFieldsSettings = useMemo((): EntitiesListingCompProps['tableViewSettings']['fieldSettings'] => {

        // Check to see if there is entityClasses selected in the filter. If there is EXACTLY 1 entity class selected then don't
        // need to show the entityClass column. If there is no entityClasses selected or there are more than 1 then we will show
        // the entityClass column UNLESS prop.tableViewHiddenFields.entityClass is set to true.
        const entityClassesFilter = searchParams.otherFields.entityClassesFilter;
        const hideEntityClassColumn = entityClassesFilter && entityClassesFilter.length === 1 ? true : undefined;

        // Get the list of selected entity classes in the filter.
        const selectedEntityClasses = searchParams.otherFields.entityClassesFilter;

        // Get the table view settings per entity classes from the project's UI Settings
        const tableViewSettingsPerEntityClasses = usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.tableViewPerEntityClasses
            : entitiesFeatureSettings?.viewsSettings?.manageView?.tableViewPerEntityClasses;

        // Get the project's global table view settings from the project's UI Settings
        const projectTableViewSettings = usageMode === UsageMode.VIEW_DATA
            ? entitiesFeatureSettings?.viewsSettings?.dataView?.tableView
            : entitiesFeatureSettings?.viewsSettings?.manageView?.tableView;

        // Get the tableViewSettings from the "tableViewSettingsPerEntityClasses" that is configured for the selectedEntityClasses
        const perEntityClassesSettings = getTableViewSettings(selectedEntityClasses, tableViewSettingsPerEntityClasses);

        // Now pick the hiddenFields, showAttributesSettings, and fieldsOrder settings from either the "perEntityClassesSettings" or the
        // global project settings. We will try to use the specific settings for entity classes first if possible
        const hiddenFields = perEntityClassesSettings?.settings.hiddenFields ?? projectTableViewSettings?.hiddenFields;
        const showAttributesSettings = perEntityClassesSettings?.settings.showAttributesSettings ?? projectTableViewSettings?.showAttributesSettings;
        const showDescendantCountsSettings = perEntityClassesSettings?.settings.showDescendantCountsSettings ?? projectTableViewSettings?.showDescendantCountsSettings;
        const fieldsOrder = perEntityClassesSettings?.settings.fieldsOrder ?? projectTableViewSettings?.fieldsOrder;


        // Compile a map of tableViewHiddenFields using data from different settings. First, use the parent container's settings, then use the
        // project settings, and lastly use the DEFAULT settings.
        const tableViewHiddenFields: TableViewHiddenFields = {
            entityId: props.tableViewHiddenFields?.entityId ?? hiddenFields?.entityId
                            ?? DEFAULT_TABLE_HIDDEN_FIELDS.entityId,
            name       : props.tableViewHiddenFields?.name ?? hiddenFields?.name ?? DEFAULT_TABLE_HIDDEN_FIELDS.name,
            description: props.tableViewHiddenFields?.description ?? hiddenFields?.description
                            ?? DEFAULT_TABLE_HIDDEN_FIELDS.description,
            parentId: props.tableViewHiddenFields?.parentId ?? hiddenFields?.parentId
                            ?? DEFAULT_TABLE_HIDDEN_FIELDS.parentId,
            entityClass: hideEntityClassColumn ?? props.tableViewHiddenFields?.entityClass ?? hiddenFields?.entityClass
                            ?? DEFAULT_TABLE_HIDDEN_FIELDS.entityClass,
            homeId   : props.tableViewHiddenFields?.homeId ?? hiddenFields?.homeId ?? DEFAULT_TABLE_HIDDEN_FIELDS.homeId,
            icon     : props.tableViewHiddenFields?.icon ?? hiddenFields?.icon ?? DEFAULT_TABLE_HIDDEN_FIELDS.icon,
            image    : props.tableViewHiddenFields?.image ?? hiddenFields?.image ?? DEFAULT_TABLE_HIDDEN_FIELDS.image,
            updatedAt: props.tableViewHiddenFields?.updatedAt ?? hiddenFields?.updatedAt
                            ?? DEFAULT_TABLE_HIDDEN_FIELDS.updatedAt,
            createdAt: props.tableViewHiddenFields?.createdAt ?? hiddenFields?.createdAt
                            ?? DEFAULT_TABLE_HIDDEN_FIELDS.createdAt,
        };


        const allFields: Mutable<EntitiesListingTableViewCompProps['fieldsSettings']['fields']> = {
            preview: {
                label: '',
            },
            entityId: {
                label       : capitalizeAll(trans('field.entity_id.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:entityId`,
                hidden      : tableViewHiddenFields?.entityId,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('entityId'),
            },
            name: {
                label       : capitalizeAll(trans('field.name.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:displaySettings.name`,
                hidden      : tableViewHiddenFields?.name,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('name'),
            },
            description: {
                label       : capitalizeAll(trans('field.description.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:description`,
                hidden      : tableViewHiddenFields?.description,
            },
            entityClass: {
                label       : capitalizeAll(trans('field.entity_class.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:entityClass`,
                hidden      : tableViewHiddenFields?.entityClass ?? hideEntityClassColumn,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('entityClass'),
            },
            parentId: {
                label       : capitalizeAll(trans('field.parent_entity.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:parentId`,
                hidden      : tableViewHiddenFields?.parentId,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('parentId'),
            },
            homeId: {
                label       : capitalizeAll(trans('field.home_id.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:homeId`,
                hidden      : tableViewHiddenFields?.homeId,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('homeId'),
            },
            icon: {
                label       : capitalizeAll(trans('label.icon')),
                dataItemProp: `${EntitySortFieldType.BASIC}:icon`,
                hidden      : tableViewHiddenFields?.icon,
            },
            image: {
                label       : capitalizeAll(trans('label.image')),
                dataItemProp: `${EntitySortFieldType.BASIC}:image`,
                hidden      : tableViewHiddenFields?.image,
            },
            createdAt: {
                label       : capitalizeAll(trans('field.created_date.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:createdAt`,
                hidden      : tableViewHiddenFields?.createdAt,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('createdAt'),
            },
            updatedAt: {
                label       : capitalizeAll(trans('field.updated_date.label')),
                dataItemProp: `${EntitySortFieldType.BASIC}:updatedAt`,
                hidden      : tableViewHiddenFields?.updatedAt,
                sortable    : SORTABLE_BASIC_ENTITY_ATTRIBUTES.includes('updatedAt'),
            },
        };


        // This is the default ordering of the table fields. The fields will be displayed in this order.
        const defaultFieldsOrder = [
            'entityId', 'name', 'description', 'entityClass', 'parentId', 'homeId', 'icon', 'image', 'createdAt', 'updatedAt',
        ];


        // if "showAttributesSettings" is configured, we will loop through all the "attributesToDisplay" and add a column for each attribute
        if (showAttributesSettings) {
            attributesToDisplay.forEach((attribute) => {
                const fieldName = `${ENTITY_CUSTOM_ATTRIBUTES_COLUMN_PREFIX}.${attribute.key}`;
                const unitKey = showAttributesSettings.settingsPerAttribute?.[attribute.key]?.unitKey;

                // Add the column settings for the attribute
                allFields[fieldName] = {
                    label        : attribute.label,
                    dataItemProp : `${EntitySortFieldType.ATTRIBUTES}:${attribute.key}`,
                    sortable     : SORTABLE_CUSTOM_ATTRIBUTE_TYPES.includes(attribute.type),
                    decimalPlaces: showAttributesSettings.settingsPerAttribute?.[attribute.key]?.decimalPlaces,
                    unit         : unitKey ? trans(unitKey) : undefined,
                };

                // Add the attribute name to the list of fields to be shown
                defaultFieldsOrder.push(fieldName);
            });
        }


        // if "showDescendantCountsSettings" is configured, we will loop through all the "descendantCountsToDisplay" and add a column for each descendant
        if (showDescendantCountsSettings) {
            descendantCountsToDisplay.forEach((descendantName) => {
                const fieldName = `${ENTITY_DESCENDANT_COUNTS_COLUMN_PREFIX}.${descendantName}`;

                const entityClass = entityClasses?.find((entityClass) => {
                    return entityClass.entityClass === descendantName;
                });

                // Add the column settings for the descendant
                allFields[fieldName] = {
                    label       : entityClass?.displaySettings.name ?? descendantName,
                    dataItemProp: `${EntitySortFieldType.DESCENDANT_COUNTS}:${descendantName}`,
                    sortable    : true,
                };

                // Add the descendant name to the list of fields to be shown
                defaultFieldsOrder.push(fieldName);
            });
        }


        // Create a array of field names in the order that we need to display. We will try to use the fieldsOrder specified in the project settings
        // first if it is configured. If not, we will use the default fieldsOrder.
        const customFieldsOrder = Array.from(new Set<string>([...(fieldsOrder ?? []), ...defaultFieldsOrder]));

        // Now create the fields to be passed to the Table. Order the fields based on the "customFieldsOrder"
        const sortedFields = customFieldsOrder.reduce((result, fieldName) => {
            if (allFields[fieldName]) {
                return {
                    ...result,
                    [fieldName]: allFields[fieldName],
                };
            }
            return result;
        }, {
            preview: allFields.preview,
        });


        return {
            fields            : sortedFields,
            onSortByField     : onSortColumnClicked,
            currentSortedField: searchParams.otherFields.sortField,
            currentSortedOrder: searchParams.otherFields.sortOrder,
        };
    }, [searchParams.otherFields.entityClassesFilter, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder, usageMode,
        entitiesFeatureSettings?.viewsSettings?.dataView?.tableViewPerEntityClasses, entitiesFeatureSettings?.viewsSettings?.dataView?.tableView,
        entitiesFeatureSettings?.viewsSettings?.manageView?.tableViewPerEntityClasses, entitiesFeatureSettings?.viewsSettings?.manageView?.tableView,
        props.tableViewHiddenFields?.entityId, props.tableViewHiddenFields?.name, props.tableViewHiddenFields?.description,
        props.tableViewHiddenFields?.parentId, props.tableViewHiddenFields?.entityClass, props.tableViewHiddenFields?.homeId,
        props.tableViewHiddenFields?.icon, props.tableViewHiddenFields?.image, props.tableViewHiddenFields?.updatedAt,
        props.tableViewHiddenFields?.createdAt, trans, descendantCountsToDisplay, onSortColumnClicked, attributesToDisplay, entityClasses]);


    /**
     * Create a cache the PAGINATION components for the Table view. This will be passed to the EntitiesListingComp later
     */
    const entitiesListingCompTableViewPaginationComp = useMemo((): EntitiesListingCompProps['tableViewSettings']['paginationComp'] => {
        const currentPage = searchParams.currentPage;
        const subRootEntityNode = subRootEntity ? findSelectedEntityNode(subRootEntity.entityId, localizedRootEntity?.[0])?.actualValue : undefined;
        const rootNode = subRootEntityNode ?? rootEntityNode;
        return (
            <PaginationBar {...{
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                currentPage,
                hasMoreItems    : currentPage < rootNode.numberOfPagesFetched - 1 || rootNode.hasMoreChildren,
                texts           : {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange: (value: number) => {
                    // When the user select a different page size, we need to reset the current page to 0
                    onPage(0);
                    onItemsPerPageChange(value);

                    // Also need to clear all entities from cache to have the component reload all data.
                    appDataDispatch(clearEntities(props.projectId));
                },
                onNextPage: () => {
                    onNextPage();
                    const nextPageNumber = currentPage + 1;
                    if (rootNode.numberOfPagesFetched <= nextPageNumber) {
                        fetchNextPageOfChildEntities(rootNode.entity.entityId);
                    }
                },
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.currentPage, searchParams.itemsPerPage, subRootEntity, localizedRootEntity, rootEntityNode, trans, onPrevPage, onPage,
        onItemsPerPageChange, appDataDispatch, props.projectId, onNextPage, fetchNextPageOfChildEntities]);


    /**
     * Create a cache the COMBINED entitiesListingCompTableViewFieldsSettings and entitiesListingCompTableViewPaginationComp
     */
    const entitiesListingCompTableViewSettings = useMemo((): EntitiesListingCompProps['tableViewSettings'] => {
        return {
            fieldSettings : entitiesListingCompTableViewFieldsSettings,
            paginationComp: entitiesListingCompTableViewPaginationComp,
            currentPage   : searchParams.currentPage,
            itemsPerPage  : searchParams.itemsPerPage,
        };
    }, [entitiesListingCompTableViewFieldsSettings, entitiesListingCompTableViewPaginationComp, searchParams.currentPage, searchParams.itemsPerPage]);


    /**
     * Create a cache the 'texts' required for the EntitiesListingComp
     */
    const entitiesListingCompTexts = useMemo((): EntitiesListingCompProps['texts'] => {
        return {
            loadMoreLabel: trans('label.load_more'),
        };
    }, [trans]);

    /**
     * On View Type changed from the control bar, update the current state's viewType.
     * Also, if the view type changed from TREE to either GRID or LIST then we will check if there is an entity currently selected. If there
     * is, we will automatically make it the current root entity so that the user will automatically see that entity's children.
     */
    const onViewTypeSelectedHandler = useCallback((viewType: EntitiesListingViewType) => {
        setSelectedViewType((currentType) => {
            // Automatically set the currently selected entity as root node but if this is confusing to the user, we can remove this logic.
            if (currentType === EntitiesListingViewType.TREE && selectedEntityNode && selectedEntityNode.children.length > 0) {
                setSubRootEntity(selectedEntityNode.entity);

                // When sub root changed, need to reset the table current page
                onPage(0);
            }

            return viewType;
        });
    }, [onPage, selectedEntityNode]);

    /**
     * On "Create Entity" button clicked from the control bar. We will open the Entity Details Preview panel but will pass "undefined"
     * entity to trigger Create entity flow.
     */
    const onCreateEntityButtonClickedHandler = useCallback(() => {
        onPreviewEntityNodeClicked(undefined);
    }, [onPreviewEntityNodeClicked]);

    /**
     * On go to absolute root entity button clicked. We will set the rootEntityNode as the current root.
     */
    const onGoToRootEntityClickHandler = useCallback(() => {
        setSubRootEntity(undefined);

        // When sub root changed, need to reset the table current page
        onPage(0);
    }, [onPage]);

    /**
     * On entity selected from the control bar. We will change the sub root entity to this entity
     */
    const onGotoEntityHandler = useCallback((entity: Entity) => {
        setSubRootEntity(entity);

        // When sub root changed, need to reset the table current page
        onPage(0);
    }, [onPage]);

    /**
     * On change root entity event from the Card view. We will change the sub root entity to this entity ONLY if the selected sub root entity
     * has children. If the selected root entity DOES NOT have children, we will take the user to the entity details page.
     * We will also dispatch an event to fetch the selected entity's children we have not done so.
     */
    const onChangeSubRootEntityHandler = useCallback(async (entityNode: EntityNode) => {
        // If the user is in VIEW_DATA mode, selecting an entity will automatically take the user to the entity details page
        if (usageMode === UsageMode.VIEW_DATA) {
            RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, entityNode.entity.entityId);
        }

        if (entityNode.children.length === 0 && entityNode.numberOfPagesFetched === 0) {
            // If we DON'T know if the selected entity has any children AND we have not fetched children for it yet then make a quick API call to
            // fetch its children. All we need is to fetch 1 child for the selected entity.

            try {
                const children = await AppAPI.getInstance().getEntities(props.projectId, {
                    parentId: entityNode.entity.entityId,
                    skip    : 0,
                    limit   : 1,
                });

                // If the result is that the entity DOES NOT have children then don't set it as subRoot because it will just going to take the user
                // to an empty page with no children. We will treat it like selecting an entity instead
                if (!children || children.items.length === 0) {
                    onEntityNodeSelected(entityNode);
                    return;
                }
            } catch (error) {
                // API call failed, do nothing
            }

            // If we get here, that mean the API call failed or the selected entity DOES have children so continue it as usual. Dispatch an
            // event to fetch the selected's entities next page of children
            fetchNextPageOfChildEntities(entityNode.entity.entityId);
        }

        if (entityNode.children.length === 0 && entityNode.numberOfPagesFetched > 0) {
            // If the selected entity DOES NOT have children AND we have already tried fetching its children then we can assume this entity
            // DOES NOT have children. We will treat it like selecting an entity instead
            onEntityNodeSelected(entityNode);
            return;
        }

        setSubRootEntity(entityNode.entity);

        // When sub root changed, need to reset the table current page
        onPage(0);
    }, [fetchNextPageOfChildEntities, onEntityNodeSelected, onPage, props.projectId, usageMode]);



    /**
     * Create the settings for the Finder Button to be passed to the EntitiesListingControlBar
     */
    const finderButton = useMemo((): EntitiesListingControlBarProps['finderButton'] => {
        if (usageMode === UsageMode.VIEW_DATA) {
            return {
                label  : trans('label.finder'),
                icon   : 'account_tree',
                onClick: () => {
                    const action: SelectEntityCompOptions = {
                        location : SlideOutPanelLocation.RIGHT,
                        compType : SlideOutPanelCompType.SELECT_ENTITY,
                        compProps: {
                            data: {
                                projectId: props.projectId,
                            },
                            onEntitySelected: (entity) => {
                                RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, entity.entityId);
                            },
                        },
                    };
                    uiDispatch(showSlideOutPanel(action));
                },
            };
        }
        return undefined;
    }, [props.projectId, trans, uiDispatch, usageMode]);


    /**
     * Create the settings for the Info Button to be passed to the EntitiesListingControlBar
     */
    const infoButton = useMemo((): EntitiesListingControlBarProps['infoButton'] => {
        if (usageMode === UsageMode.VIEW_DATA) {
            return {
                label   : trans('label.info'),
                icon    : 'info',
                disabled: !selectedEntityNode,
                onClick : () => {
                    if (selectedEntityNode) {
                        onPreviewEntityNodeClicked(selectedEntityNode);
                    }
                },
            };
        }
        return undefined;
    }, [onPreviewEntityNodeClicked, selectedEntityNode, trans, usageMode]);


    /**
     * Create the settings for the Info Button to be passed to the EntitiesListingControlBar
     */
    const createEntityButton = useMemo((): EntitiesListingControlBarProps['createEntityButton'] => {
        if (usageMode === UsageMode.MANAGE_DATA && canEditEntity) {
            return {
                label  : trans('entity.action.create_entity'),
                icon   : 'add',
                color  : 'primary',
                onClick: () => {
                    onCreateEntityButtonClickedHandler();
                },
            };
        }
        return undefined;
    }, [canEditEntity, onCreateEntityButtonClickedHandler, trans, usageMode]);


    /**
     * Create the controlBar and cache it. We will add this to the Top Nav if "stickyControlBar". If not, we will add it as part of the
     * container.
     */
    const controlBar = useMemo(() => {
        const enableChangeViewOptions = (() => {
            if (usageMode === UsageMode.MANAGE_DATA) {
                // In "Manage" mode, the View options is always enabled UNLESS the "enableViewOptions" is specifically set to false
                if (entitiesFeatureSettings?.viewsSettings?.manageView?.enableChangeViewOptions === false) {
                    return false;
                }
                return true;
            }
            if (usageMode === UsageMode.VIEW_DATA) {
                // In "View" mode, the View options is always disabled UNLESS the "enableViewOptions" is specifically set to true
                if (entitiesFeatureSettings?.viewsSettings?.dataView?.enableChangeViewOptions === true) {
                    return true;
                }
            }
            return false;
        })();

        const enableSearchOptions = (() => {
            if (usageMode === UsageMode.MANAGE_DATA) {
                // In "Manage" mode, the search options is always enabled UNLESS the "enableSearchOption" is specifically set to false
                if (entitiesFeatureSettings?.viewsSettings?.manageView?.enableSearchOptions === false) {
                    return false;
                }
                return true;
            }
            if (usageMode === UsageMode.VIEW_DATA) {
                // In "View" mode, the search options is always disabled UNLESS the "enableSearchOption" is specifically set to true
                if (entitiesFeatureSettings?.viewsSettings?.dataView?.enableSearchOptions === true) {
                    return true;
                }
            }
            return false;
        })();

        return (
            <EntitiesListingControlBar
                key={`${location.pathname}${location.search}`}
                className={classes.controlBarContainer}
                projectId={props.projectId}
                selectedViewType={selectedViewType}
                finderButton={finderButton}
                infoButton={infoButton}
                createEntityButton={createEntityButton}
                showViewTypeOptions={enableChangeViewOptions}
                onViewTypeSelected={onViewTypeSelectedHandler}
                searchAndFilterSettings={enableSearchOptions ? entitiesListingCompFilters : undefined}
            />
        );
    }, [location.pathname, location.search, classes.controlBarContainer, props.projectId, selectedViewType, finderButton, infoButton,
        createEntityButton, onViewTypeSelectedHandler, entitiesListingCompFilters, usageMode,
        entitiesFeatureSettings?.viewsSettings?.manageView?.enableChangeViewOptions,
        entitiesFeatureSettings?.viewsSettings?.manageView?.enableSearchOptions,
        entitiesFeatureSettings?.viewsSettings?.dataView?.enableChangeViewOptions,
        entitiesFeatureSettings?.viewsSettings?.dataView?.enableSearchOptions]);



    /**
     * This useEffect is used for attaching the globalSearchBar to the Top Nav bar when this component initialize and also
     * remove the globalSearchBar when this component is destroyed
     */
    useEffect(() => {
        // Only attach the globalSearchBar to the top nav if we want it to be sticky
        if (props.stickyControlBar) {
            uiDispatch(addControlPanelComp(ENTITY_LISTING_PAGE_CONTROL_BAR, controlBar, ControlPanelCompPriority.HIGH));
        }

        // Return a clean up function which to remove the globalSearchBar when the component is destroyed
        return () => {
            if (props.stickyControlBar) {
                uiDispatch(removeControlPanelComp(ENTITY_LISTING_PAGE_CONTROL_BAR));
            }
        };
    }, [controlBar, props.stickyControlBar, uiDispatch]);



    return (
        <div className={clsx(classes.root, props.className)}>
            {!props.minimalUI && !props.stickyControlBar && (
                controlBar
            )}

            {/**
             * If in MANAGE_DATA mode and in GRID or LIST or TABLE view type, show the breadcrumb bar
             */}
            {usageMode === UsageMode.MANAGE_DATA && pathToSubRootEntity.length > 0
                && (selectedViewType === EntitiesListingViewType.LIST
                    || selectedViewType === EntitiesListingViewType.GRID
                    || selectedViewType === EntitiesListingViewType.TABLE
                ) && (
                <EntityBreadcrumbBar
                    className={classes.breadcrumbBar}
                    entitiesBreadcrumbs={pathToSubRootEntity}
                    onGoHomeClick={onGoToRootEntityClickHandler}
                    onGotoEntity={onGotoEntityHandler}
                />
            )}

            <div className={clsx(classes.entitiesListingCompWrapper, props.minimalUI && 'minimalUI')}>
                <EntitiesListingComp
                    className={classes.entitiesListingComp}
                    projectId={props.projectId}
                    style={props.style}
                    icon={trans('icons.entity')}
                    title={capitalizeFirst(trans('entities.entities_listing.panel.title'))}
                    viewType={selectedViewType}
                    treeViewSettings={entitiesListingTreeViewSettings}
                    cardViewSettings={entitiesListingCompCardViewSettings}
                    tableViewSettings={entitiesListingCompTableViewSettings}
                    texts={entitiesListingCompTexts}
                    subRootEntityId={subRootEntity?.entityId}
                    onChangeSubRootEntity={onChangeSubRootEntityHandler}
                    listData={localizedRootEntity}
                    onListItemSelected={onEntityNodeSelected}
                    onPreviewItemClicked={!props.disablePreviewOption ? onPreviewEntityNodeClicked : undefined}
                    selectedEntityId={selectedEntityNode?.entity.entityId}
                    showRootEntity={false}
                    noDataMessageComp={noDataMessageComp}
                    emptyDataMessageComp={emptyDataMessageComp}
                />
            </div>
        </div>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntitiesListingContainer', EntitiesListingContainer);
